/**
 * useControlTowerSession (P12)
 *
 * Reactive view of the per-user override session. Polls every 1s for
 * countdown + auto-expire detection. Session source is DB-backed with
 * in-memory cache in the session module.
 */

import { useCallback, useEffect, useState } from "react";
import {
  activateOverrideMode,
  cancelOverrideMode,
  getActiveSession,
  getRemainingMs,
  hydrateControlTowerSession,
  type ActivateInput,
  type ActivateResult,
  type ControlTowerSession,
} from "@/data/controlTower/session";

export function useControlTowerSession(adminId: string) {
  const [session, setSession] = useState<ControlTowerSession | null>(() => getActiveSession());
  const [remainingMs, setRemainingMs] = useState<number>(() => getRemainingMs());

  useEffect(() => {
    void hydrateControlTowerSession(adminId).then((s) => {
      setSession(s);
      setRemainingMs(getRemainingMs());
    });
    const tick = () => {
      setSession(getActiveSession());
      setRemainingMs(getRemainingMs());
    };
    const id = window.setInterval(tick, 1000);
    return () => {
      window.clearInterval(id);
    };
  }, [adminId]);

  const activate = useCallback(
    (input: Omit<ActivateInput, "adminId">): ActivateResult => {
      const r = activateOverrideMode({ ...input, adminId });
      if (r.ok) {
        setSession(r.session);
        setRemainingMs(getRemainingMs());
      }
      return r;
    },
    [adminId],
  );

  const cancel = useCallback(
    (killSwitch = false) => {
      cancelOverrideMode(adminId, killSwitch);
      setSession(null);
      setRemainingMs(0);
    },
    [adminId],
  );

  return {
    session,
    isActive: session !== null,
    remainingMs,
    activate,
    cancel,
  };
}
